import React, { useState } from "react";
import { ArrowRight, Radio } from "lucide-react";
import { toast } from "sonner";
import { SignedIn, SignedOut, SignInButton, useAuth } from "@clerk/clerk-react";

export default function IntakeForm({ onSubmit, loading, sweepsRemaining = null }) {
  const { isSignedIn } = useAuth();
  const [your_domain, setYour] = useState("");
  const [competitor_domain, setComp] = useState("");
  // A sweep reads a third party's site. The affirmative act belongs here, at
  // the point the request goes out, not on a page nobody visits.
  const [accepted, setAccepted] = useState(false);

  const handle = (e) => {
    e.preventDefault();
    if (!your_domain.trim() || !competitor_domain.trim()) {
      toast.error("Both domains are required");
      return;
    }
    if (!accepted) {
      toast.error("Please accept the terms before running a sweep");
      return;
    }
    onSubmit({
      your_domain: your_domain.trim(),
      competitor_domain: competitor_domain.trim(),
      accept_terms: true,
    });
  };

  return (
    <form
      id="intake"
      onSubmit={handle}
      className="card-cream p-6 md:p-8"
      data-testid="intake-form"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Radio size={14} className="text-[#e88a51] blink" />
          <span className="tick text-[#171717]">Initiate Margin Risk Sweep</span>
        </div>
        <span className="tick">01 / Intake</span>
      </div>

      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        <div>
          <label className="tick block mb-2" htmlFor="your-domain">Your Storefront Domain</label>
          <input
            id="your-domain"
            data-testid="intake-your-domain"
            className="input-vault"
            placeholder="yourbrand.com"
            value={your_domain}
            onChange={(e) => setYour(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div>
          <label className="tick block mb-2" htmlFor="comp-domain">Competitor / Cohort Domain</label>
          <input
            id="comp-domain"
            data-testid="intake-competitor-domain"
            className="input-vault"
            placeholder="competitorbrand.com"
            value={competitor_domain}
            onChange={(e) => setComp(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>

      {/* Said before the sweep runs, not discovered inside the briefing.
          Many brands run a separate storefront per market: bondisands.com
          serves USD, bondisands.com.au serves AUD, and they carry different
          ranges at different prices. Sweeping across the two returns a real
          measurement of the wrong thing - and because we refuse to convert
          currencies, the price comparisons are then withheld and the reader
          gets a thinner briefing for a reason they could have avoided. */}
      <p className="mt-3 font-mono-tech text-[11px] text-[#525252] leading-relaxed"
         data-testid="intake-same-market-note">
        Use storefronts from the same market. Many brands run one store per
        country — <span className="text-[#404040]">bondisands.com</span> prices in
        USD while <span className="text-[#404040]">bondisands.com.au</span> prices
        in AUD — and prices in different currencies cannot be compared, so those
        sections are withheld rather than converted.
      </p>

      <label className="mt-6 flex items-start gap-2.5 cursor-pointer group/consent">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          data-testid="intake-accept-terms"
          className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#e88a51] cursor-pointer"
        />
        <span className="font-mono-tech text-[11px] text-[#525252] leading-relaxed">
          I agree to the{" "}
          <a href="/legal/terms" target="_blank" rel="noopener noreferrer"
             className="text-[#9a3412] underline underline-offset-2 hover:text-[#171717]">Terms of Service</a>{" "}
          and{" "}
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer"
             className="text-[#9a3412] underline underline-offset-2 hover:text-[#171717]">Privacy Policy</a>,
          and I have read{" "}
          <a href="/legal/methodology" target="_blank" rel="noopener noreferrer"
             className="text-[#9a3412] underline underline-offset-2 hover:text-[#171717]">how Margin Lens collects data</a>.
        </span>
      </label>

      <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <p className="font-mono-tech text-[11px] text-[#525252] max-w-md">
          Public schema extraction only. No store credentials required.
          {" "}
          {isSignedIn
            ? (sweepsRemaining === null
                ? "Your briefing is saved to your account."
                : `${sweepsRemaining} free ${sweepsRemaining === 1 ? "sweep" : "sweeps"} left on your account.`)
            : "Sign in to run a sweep — each briefing is saved to your account."}
        </p>

        {/* The account comes before the sweep, not before the briefing. A sweep
            is a real model call and two catalogue reads; running it for an
            anonymous visitor spent money on someone we could not come back to,
            and left a report nobody could open. */}
        <SignedIn>
          <button
            type="submit"
            disabled={loading || !accepted}
            data-testid="intake-submit"
            className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Sweeping…" : (<>Run sweep <ArrowRight size={14} /></>)}
          </button>
        </SignedIn>
        <SignedOut>
          <SignInButton mode="modal">
            <button type="button" data-testid="intake-signin" className="btn-primary">
              Sign in to run a sweep <ArrowRight size={14} />
            </button>
          </SignInButton>
        </SignedOut>
      </div>
    </form>
  );
}
